import React, { useState, useEffect, useCallback } from 'react';
import './TimeframeSelector.css';
import { logger } from '../../utils/logger';

export interface TimeframeOption {
  interval: string;
  normalized_interval: string;
  count: number;
  time_range?: {
    start: number;
    end: number;
  };
  available: boolean;
  aggregated?: boolean;
}

export interface TimeframeSelectorProps {
  symbol?: string;
  selectedTimeframe: string;
  onTimeframeChange: (timeframe: string) => void;
  className?: string;
}

const TimeframeSelector: React.FC<TimeframeSelectorProps> = ({
  symbol = 'BTCUSDT',
  selectedTimeframe,
  onTimeframeChange,
  className = ''
}) => {
  const [availableTimeframes, setAvailableTimeframes] = useState<TimeframeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get available timeframes based on symbol
  const getAvailableTimeframes = () => {
    const allTimeframes = [
      { interval: '1m', normalized_interval: '1m', count: 0, available: false },
      { interval: '5m', normalized_interval: '5m', count: 0, available: false },
      { interval: '30m', normalized_interval: '30m', count: 0, available: false },
      { interval: '1h', normalized_interval: '1h', count: 0, available: false },
      { interval: '4h', normalized_interval: '4h', count: 0, available: false },
      { interval: '1d', normalized_interval: '1d', count: 0, available: false }
    ];

    // Enable timeframes based on symbol
      if (symbol.endsWith('USDT')) {
      // Default for other crypto symbols
      allTimeframes.forEach(tf => (tf.available = ['1m', '5m', '30m', '1h', '4h', '1d'].includes(tf.interval)));
    }

    return allTimeframes;
  };

  // Fetch available timeframes based on symbol
  const fetchAvailableTimeframes = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Use the predefined timeframes based on symbol
      const timeframes = getAvailableTimeframes();
      setAvailableTimeframes(timeframes);
    } catch (err) {
      logger.error('Failed to get available timeframes:', err);
      setError(err instanceof Error ? err.message : 'Failed to get timeframes');
      setAvailableTimeframes(getAvailableTimeframes());
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  useEffect(() => {
    fetchAvailableTimeframes();
  }, [fetchAvailableTimeframes]);

  const formatTimeRange = (timeRange: { start: number; end: number }) => {
    const start = new Date(timeRange.start * 1000);
    const end = new Date(timeRange.end * 1000);

    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    };

    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const formatCount = (count: number) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  const getTimeframeLabel = (interval: string) => {
    const labels: { [key: string]: string } = {
      '1m': '1M', '1min': '1M',
      '5m': '5M', '5min': '5M',
      '30m': '30M', '30min': '30M',
      '1h': '1H', '1hour': '1H',
      '4h': '4H', '4hour': '4H',
      '1d': '1D', '1day': '1D',
      '1w': '1W', '1week': '1W',
      '1y': '1Y', '1year': '1Y'
    };
    return labels[interval] || interval;
  };

  if (loading) {
    return (
      <div className={`timeframe-selector ${className}`}>
        <div className="timeframe-selector-loading">
          <span>Loading timeframes...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`timeframe-selector ${className}`}>
        <div className="timeframe-selector-error">
          <span>⚠️ {error}</span>
          <button onClick={fetchAvailableTimeframes} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`timeframe-selector ${className}`}>
      <div className="timeframe-options">
        {availableTimeframes.map((timeframe) => (
          <button
            key={timeframe.interval}
            className={`timeframe-option ${
              selectedTimeframe === timeframe.interval ? 'active' : ''
            } ${!timeframe.available ? 'unavailable' : ''}`}
            onClick={() => timeframe.available && onTimeframeChange(timeframe.interval)}
            disabled={!timeframe.available}
            title={
              timeframe.available && timeframe.time_range
                ? `${getTimeframeLabel(timeframe.interval)} (${formatCount(timeframe.count)} candles, ${formatTimeRange(timeframe.time_range)})`
                : `${getTimeframeLabel(timeframe.interval)} (No data available)`
            }
          >
            <span className="timeframe-label">
              {getTimeframeLabel(timeframe.interval)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default TimeframeSelector;
